import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Button, ChevronDownIcon } from '@selina-finance/ui';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import {
    DebtLabelEnum,
    newDebtFormItems,
    unsecuredTypes,
} from '@src/app/formModels/eligibility/debtCon';
import {
    CategoryPortal,
    CreditCommitmentsData,
    Status,
} from '@src/app/interfaces/debtcon';
import { isCreditCardOrMailOrder } from '@src/shared/helpers/debtCon';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
} from '@src/shared/helpers/input';
import { HttpError } from '@src/ui/components/HttpError';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';

import CommonQuestions from './CommonQuestions';
import CreditCardMailOrderForm from './CreditCardMailOrderForm';

export const handleDebtTypeChange = (
    e: ChangeEvent<HTMLInputElement>,
    formikSetFieldValue: (field: string, value: any) => void,
    currentValue: DebtLabelEnum,
    setCategory: Dispatch<SetStateAction<CategoryPortal>>,
) => {
    const value = e.currentTarget.value as DebtLabelEnum;

    const getCategory = (type: DebtLabelEnum) =>
        unsecuredTypes.includes(type)
            ? CategoryPortal.Unsecured
            : CategoryPortal.MortgagesAndSecured;

    const newCategory = getCategory(value);
    const currentCategory = getCategory(currentValue);

    if (newCategory !== currentCategory) {
        formikSetFieldValue('lenderName', null);
    }

    formikSetFieldValue('type', value);
    setCategory(newCategory);
};

const NewDebtInputView = () => {
    const navigate = useNavigate();

    const { setCurrentForm, applicants } = useContext(ApplicationContext);
    const { setCreditCommitmentsData, createCreditCommitment } =
        useContext(DebtConContext);

    const formikRef = useRef<any>(null);
    const [error, setError] = useState<string | null>(null);

    const [_, setCategory] = useState<CategoryPortal>(CategoryPortal.Unsecured);

    const formItems = newDebtFormItems(
        applicants,
        formikRef.current?.values,
        false,
    );

    const defaultValues = getDefaultValues(formItems);

    const validationSchema = Yup.lazy((values: any) => {
        const items = newDebtFormItems(
            applicants,
            values,
            isCreditCardOrMailOrder(values?.type),
        );
        return getValidationSchema(items);
    });

    useEffect(() => {
        if (formikRef.current) {
            setCurrentForm(formikRef.current);
        } else {
            setCurrentForm(undefined);
        }
    }, [formikRef, setCurrentForm]);

    const addDebt = async (values: CreditCommitmentsData) => {
        values.status = Status.Active;

        if (values.owner === 'Joint') {
            values.joint = true;
        }

        const response = await createCreditCommitment(values);
        if (!response?.success) {
            setError(response.error);
        } else {
            setCreditCommitmentsData((prevDebts: CreditCommitmentsData[]) => [
                ...prevDebts,
                { ...values, id: response[0].id },
            ]);

            navigate('/eligibility/debt-con/review');
        }
    };

    return (
        <div className='text-steel-100 flex flex-col mx-auto max-w-400 w-full my-6'>
            <div
                className='p-4 rounded-lg bg-monochrome-100 debt-shadow'
                style={{
                    boxShadow:
                        '0px 0px 4px 0px rgba(0, 0, 0, 0.15), 0px 0px 0px 5px rgba(255, 255, 255, 0.90)',
                }}
            >
                <p className='text-28 font-bold leading-120% mb-4'>
                    Add a new debt
                </p>

                <Formik
                    enableReinitialize
                    initialValues={{ ...defaultValues }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        addDebt(values);
                    }}
                    innerRef={(ref) => (formikRef.current = ref)}
                >
                    {(formikProps) => {
                        const creditCardOrMailOrder = isCreditCardOrMailOrder(
                            formikProps.values.type,
                        );

                        const liveFormItems = newDebtFormItems(
                            applicants,
                            formikProps.values,
                            creditCardOrMailOrder,
                        );

                        const { getError } = getFormHelpers(
                            liveFormItems,
                            formikProps,
                        );

                        const sharedProps = {
                            values: formikProps.values,
                            touched: formikProps.touched,
                            setTouched: formikProps.setTouched,
                            setFieldValue: formikProps.setFieldValue,
                            handleChange: formikProps.handleChange,
                            handleBlur: formikProps.handleBlur,
                            getError,
                            debt: formikProps.values,
                        };

                        return (
                            <Form>
                                <div className='grid gap-6 mt-4'>
                                    {applicants.length === 2 && (
                                        <SelectBox
                                            labelSize='small'
                                            label='Owner of this debt'
                                            items={formItems.owner.items}
                                            suffix={<ChevronDownIcon />}
                                            name='owner'
                                            validState={
                                                !!formikProps.values.owner
                                            }
                                            error={getError('owner')}
                                            value={
                                                formikProps.values.owner || ' '
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) => {
                                                formikProps.setFieldValue(
                                                    'owner',
                                                    e.currentTarget.value,
                                                );
                                            }}
                                            onBlur={formikProps.handleBlur}
                                        />
                                    )}
                                    <SelectBox
                                        labelSize='small'
                                        label='Type of debt'
                                        items={formItems.type.items}
                                        suffix={<ChevronDownIcon />}
                                        name='type'
                                        validState={!!formikProps.values.type}
                                        error={getError('type')}
                                        value={formikProps.values.type || ' '}
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            handleDebtTypeChange(
                                                e,
                                                formikProps.setFieldValue,
                                                formikProps.values.type,
                                                setCategory,
                                            )
                                        }
                                        onBlur={formikProps.handleBlur}
                                    />
                                    {formikProps.values.type && (
                                        <CommonQuestions
                                            {...sharedProps}
                                            formItems={formItems}
                                        />
                                    )}
                                    {creditCardOrMailOrder && (
                                        <CreditCardMailOrderForm
                                            {...sharedProps}
                                            errors={formikProps.errors}
                                        />
                                    )}
                                </div>
                                <Button
                                    size='large'
                                    color='secondary'
                                    className='font-bold w-full mt-10 mb-7 text-lg'
                                    type='submit'
                                >
                                    <span className='text-lg'>Continue</span>
                                </Button>

                                <div className='flex justify-center'>
                                    <span
                                        onClick={() =>
                                            navigate(
                                                '/eligibility/debt-con/review',
                                            )
                                        }
                                        className='text-lg cursor-pointer font-bold'
                                    >
                                        Cancel
                                    </span>
                                </div>
                                {error && <HttpError error={error} />}
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default NewDebtInputView;
